// Whimsigoth moonlight cursor (design-system §4, mirror byte-true math):
// a masked "lit" copy of the triptych + a soft-light tint follow the pointer
// with lerp .09; after 3s idle (or on touch) the light drifts on a slow sine
// path. Markup contract:
//   <skullette-moonlight>
//     …base triptych…
//     <div data-moon-lit>…lit copy (masked)…</div>
//     <div data-moon-tint>…soft-light radial…</div>
//   </skullette-moonlight>
// Reduced motion: no rAF loop - the server-rendered static mask stays.
// No-JS: same static mask. Radius 300px, mask stops 32%/72% (LOCK).
class SkulletteMoonlight extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const lit = this.querySelector('[data-moon-lit]');
    const tint = this.querySelector('[data-moon-tint]');
    if (!lit || !tint) return;

    const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    let last = performance.now();
    let inside = false;
    let px = null, py = null, tx = null, ty = null;

    this._onMove = (e) => {
      const r = this.getBoundingClientRect();
      tx = e.clientX - r.left;
      ty = e.clientY - r.top;
      last = performance.now();
      inside = true;
    };
    this._onLeave = () => { inside = false; };
    this.addEventListener('pointermove', this._onMove);
    this.addEventListener('pointerleave', this._onLeave);

    const frame = (now) => {
      const r = this.getBoundingClientRect();
      const litR = lit.getBoundingClientRect();
      const W = r.width, H = r.height;
      const cx = W / 2, cy = H * 0.42;
      if (px == null) { px = cx; py = cy; tx = cx; ty = cy; }
      const idle = touch || !inside || now - last > 3000;
      if (idle) {
        const t = now / 1000;
        tx = cx + Math.sin(t * 0.42) * W * 0.30;
        ty = cy + Math.sin(t * 0.31 + 1.1) * H * 0.16;
      }
      px += (tx - px) * 0.09;
      py += (ty - py) * 0.09;

      const mx = px - (litR.left - r.left);
      const my = py - (litR.top - r.top);
      const mask = `radial-gradient(circle 300px at ${mx}px ${my}px, black 32%, transparent 72%)`;
      lit.style.webkitMaskImage = mask;
      lit.style.maskImage = mask;
      tint.style.background = `radial-gradient(circle 300px at ${px}px ${py}px, var(--moonlight-tint), transparent 70%)`;

      this._raf = requestAnimationFrame(frame);
    };
    // run the loop only while the section is on screen - a permanent rAF
    // with layout reads is too heavy for a storefront page
    this._io = new IntersectionObserver((entries) => {
      const visible = entries.some((e) => e.isIntersecting);
      if (visible && !this._raf) {
        this._raf = requestAnimationFrame(frame);
      } else if (!visible && this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    });
    this._io.observe(this);
  }

  disconnectedCallback() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._io?.disconnect();
  }
}

if (!customElements.get('skullette-moonlight')) {
  customElements.define('skullette-moonlight', SkulletteMoonlight);
}
